/**
 * NotificationBell — global in-app inbox in the TopBar. Shows an unread
 * badge; the dropdown lists recent notifications. Clicking one marks it
 * read and deep-links to the relevant card. This is the surface that
 * makes "a card sat idle because nobody saw it" impossible.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from "@/hooks/useFlow";
import type { FlowNotification } from "@/types/flow";

export function NotificationBell() {
  // Flow (incl. notifications) is admin-only for now.
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return <NotificationBellInner />;
}

function NotificationBellInner() {
  const [open, setOpen] = useState(false);
  const { data: count } = useUnreadCount();
  const { data: notifications } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  const unread = count ?? 0;
  const items = Array.isArray(notifications) ? notifications : [];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const onClickItem = (n: FlowNotification) => {
    if (!n.is_read) markRead.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-lg p-2 text-ink-muted transition-colors hover:bg-raised hover:text-ink-2"
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-line bg-surface shadow-xl">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="text-sm font-semibold text-ink-2">Notifications</span>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-ink-subtle">
                You're all caught up.
              </p>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => onClickItem(n)}
                className={cn(
                  "flex w-full items-start gap-2 border-b border-line-soft px-4 py-3 text-left transition-colors hover:bg-raised",
                  !n.is_read && "bg-indigo-50/40 dark:bg-indigo-500/10",
                )}
              >
                <span
                  className={cn(
                    "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                    n.is_read ? "bg-transparent" : "bg-indigo-500",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink-2">
                    {n.title}
                  </span>
                  {n.body && (
                    <span className="mt-0.5 block truncate text-xs text-ink-muted">
                      {n.body}
                    </span>
                  )}
                  <span className="mt-0.5 block text-[11px] text-ink-subtle">
                    {formatWhen(n.created_at)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
